import React, { useMemo } from 'react';
import AudiotrackIcon from '@material-ui/icons/Audiotrack';
import ImageIcon from '@material-ui/icons/Image';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import { extractIdFromURI } from '../../utils/resources/ids';
import useGetMedias from '../notes/hooks/useGetMedias';

export interface InstallationFile {
    media: any;
    Icon: React.ElementType;
    note: any;
    order: number;
}

/**
 * The files attached along the traceability process (PDFs generated at the end of commissioning,
 * site photos…), each with the step of the history that attached it, most recent step first.
 */
const useInstallationFiles = (notes: any[]) => {
    const stepOfMedia = useMemo(() => {
        const steps = new Map<string, { mediaId: string; note: any; order: number }>();
        notes.forEach((note: any, order: number) => {
            (note?.data?.medias || []).forEach((media: any) => {
                const key = media?.id && extractIdFromURI(media.id);
                if (key && !steps.has(key)) {
                    steps.set(key, { mediaId: media.id, note, order });
                }
            });
        });
        return steps;
    }, [notes]);

    const mediasIds = useMemo(() => Array.from(stepOfMedia.values()).map(({ mediaId }) => mediaId), [stepOfMedia]);

    const { images, audios, documents, isLoading, error } = useGetMedias({
        mediasIds,
        perPage: mediasIds.length,
    });

    const files: InstallationFile[] = useMemo(() => {
        const withIcon = (medias: any[] | undefined, Icon: React.ElementType) =>
            (medias || [])
                .filter((media) => !!media.urlToDownload)
                .map((media) => {
                    const step = stepOfMedia.get(extractIdFromURI(media.id || media['@id'] || '') || '');
                    return { media, Icon, note: step?.note, order: step?.order ?? Infinity };
                });
        return [
            ...withIcon(documents, PictureAsPdfIcon),
            ...withIcon(images, ImageIcon),
            ...withIcon(audios, AudiotrackIcon),
        ].sort((a, b) => a.order - b.order);
    }, [documents, images, audios, stepOfMedia]);

    return { files, loading: mediasIds.length > 0 && isLoading, error };
};

export default useInstallationFiles;
